import React, { useState, useEffect } from 'react';
import {
  FileJson,
  FileCode,
  Timer,
  Calendar,
  Text,
  Code2,
  History,
  Calculator,
  Clock,
  HardDrive,
  Globe,
  Monitor,
  Play,
  Pause,
  RotateCcw,
  Coffee,
  Brain,
  Check,
  Copy,
  RefreshCw,
  Clipboard
} from 'lucide-react';
import prettier from 'prettier';
import * as parser from 'prettier/parser-typescript';
import * as parserBabel from 'prettier/parser-babel';
import * as parserHtml from 'prettier/parser-html';
import * as parserCss from 'prettier/parser-postcss';
import jsYaml from 'js-yaml';

interface FormatConverterState {
  input: string;
  output: string;
  format: 'json' | 'yaml' | 'xml';
  sourceFormat: 'json' | 'yaml' | 'xml' | 'auto';
  indentation: number;
  error: string | null;
}

interface TimeUnit {
  value: number;
  unit: 'ms' | 's' | 'min' | 'h';
}

interface FileSize {
  value: number;
  unit: 'B' | 'KB' | 'MB' | 'GB' | 'TB';
}

interface SystemInfo {
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
  colorDepth: number;
  devicePixelRatio: number;
  platform: string;
  browser: string;
  browserVersion: string;
  os: string;
  cookies: boolean;
  localStorage: boolean;
  sessionStorage: boolean;
  webWorkers: boolean;
  serviceWorkers: boolean;
  webGL: boolean;
  webP: boolean;
  touchScreen: boolean;
  connection: {
    type: string;
    downlink: number | null;
    rtt: number | null;
    effectiveType: string | null;
    saveData: boolean | null;
  };
}

interface WebLoadTime {
  dns: number;
  tcp: number;
  request: number;
  response: number;
  processing: number;
  load: number;
  total: number;
}

interface PomodoroState {
  timeLeft: number;
  isRunning: boolean;
  mode: 'work' | 'shortBreak' | 'longBreak';
  cycles: number;
  settings: {
    workTime: number;
    shortBreakTime: number;
    longBreakTime: number;
    cyclesBeforeLongBreak: number;
  };
  stats: {
    completedPomodoros: number;
    completedShortBreaks: number;
    completedLongBreaks: number;
    totalWorkTime: number;
  };
}

interface FormatterConfig {
  useTabs: boolean;
  tabWidth: number;
  printWidth: number;
  semi: boolean;
  singleQuote: boolean;
  bracketSpacing: boolean;
  bracketSameLine: boolean;
  arrowParens: 'avoid' | 'always';
}

interface CodeFormatterState {
  input: string;
  output: string;
  language: 'typescript' | 'javascript' | 'html' | 'css';
  error: string | null;
  config: FormatterConfig;
}

interface ColorConverter {
  hex: string;
  rgb: string;
  hsl: string;
}

interface ResponsiveBreakpoint {
  name: string;
  minWidth: number;
  maxWidth: number | null;
}

interface SqlFormatter {
  input: string;
  output: string;
  dialect: 'mysql' | 'postgresql' | 'sqlite' | 'mssql' | 'oracle';
  error: string | null;
}

interface LengthUnit {
  value: number;
  unit: 'mm' | 'cm' | 'in' | 'm' | 'ft' | 'yd' | 'km' | 'mi';
}

interface TemperatureUnit {
  value: number;
  unit: 'celsius' | 'fahrenheit' | 'kelvin';
}

interface ColorConverterAdvanced {
  hex: string;
  rgb: string;
  rgba: string;
  hsl: string;
  hsla: string;
  cmyk: string;
  name: string;
}

interface Base64Converter {
  input: string;
  output: string;
  mode: 'encode' | 'decode';
  error: string | null;
}

interface UrlConverter {
  input: string;
  output: string;
  mode: 'encode' | 'decode';
  error: string | null;
}

interface JwtDecoder {
  token: string;
  header: string;
  payload: string;
  error: string | null;
}

interface NumberBaseConverter {
  input: string;
  fromBase: 2 | 8 | 10 | 16;
  toBase: 2 | 8 | 10 | 16;
  output: string;
  error: string | null;
}

interface HashGenerator {
  input: string;
  algorithm: 'md5' | 'sha1' | 'sha256' | 'sha512';
  output: string;
}

const DevTools: React.FC = () => {
  const [activeTab, setActiveTab] = useState('converter');
  const [unitSubTab, setUnitSubTab] = useState('timeConv');
  const [converter, setConverter] = useState<FormatConverterState>({
    input: '',
    output: '',
    format: 'json',
    sourceFormat: 'auto',
    indentation: 2,
    error: null
  });
  const [code, setCode] = useState('');
  const [timeConversion, setTimeConversion] = useState<TimeUnit>({
    value: 0,
    unit: 'ms'
  });
  
  const [convertedTimes, setConvertedTimes] = useState<{
    ms: number;
    s: number;
    min: number;
    h: number;
  }>({
    ms: 0,
    s: 0,
    min: 0,
    h: 0
  });

  const [fileSize, setFileSize] = useState<FileSize>({
    value: 0,
    unit: 'MB'
  });

  const [convertedSizes, setConvertedSizes] = useState<{
    B: number;
    KB: number;
    MB: number;
    GB: number;
    TB: number;
  }>({
    B: 0,
    KB: 0,
    MB: 0,
    GB: 0,
    TB: 0
  });

  const [systemInfo, setSystemInfo] = useState<SystemInfo>({
    userAgent: '',
    screenWidth: 0,
    screenHeight: 0,
    colorDepth: 0,
    devicePixelRatio: 1,
    platform: '',
    browser: '',
    browserVersion: '',
    os: '',
    cookies: false,
    localStorage: false,
    sessionStorage: false,
    webWorkers: false,
    serviceWorkers: false,
    webGL: false,
    webP: false,
    touchScreen: false,
    connection: {
      type: '',
      downlink: null,
      rtt: null,
      effectiveType: null,
      saveData: null
    }
  });

  const [webLoadTime, setWebLoadTime] = useState<WebLoadTime>({
    dns: 0,
    tcp: 0,
    request: 0,
    response: 0,
    processing: 0,
    load: 0,
    total: 0
  });

  const [pomodoro, setPomodoro] = useState<PomodoroState>({
    timeLeft: 25 * 60, // 25 minutes in seconds
    isRunning: false,
    mode: 'work',
    cycles: 0,
    settings: {
      workTime: 25 * 60,
      shortBreakTime: 5 * 60,
      longBreakTime: 15 * 60,
      cyclesBeforeLongBreak: 4
    },
    stats: {
      completedPomodoros: 0,
      completedShortBreaks: 0,
      completedLongBreaks: 0,
      totalWorkTime: 0
    }
  });

  const [formatter, setFormatter] = useState<CodeFormatterState>({
    input: '',
    output: '',
    language: 'typescript',
    error: null,
    config: {
      useTabs: false,
      tabWidth: 2,
      printWidth: 80,
      semi: true,
      singleQuote: true,
      bracketSpacing: true,
      bracketSameLine: false,
      arrowParens: 'always'
    }
  });

  const [colorConverter, setColorConverter] = useState<ColorConverter>({
    hex: '#3b82f6',
    rgb: 'rgb(59, 130, 246)',
    hsl: 'hsl(217, 91%, 60%)'
  });

  const [responsiveBreakpoints] = useState<ResponsiveBreakpoint[]>([
    { name: 'xs (mobile)', minWidth: 0, maxWidth: 639 },
    { name: 'sm (small)', minWidth: 640, maxWidth: 767 },
    { name: 'md (medium)', minWidth: 768, maxWidth: 1023 },
    { name: 'lg (large)', minWidth: 1024, maxWidth: 1279 },
    { name: 'xl (extra large)', minWidth: 1280, maxWidth: 1535 },
    { name: '2xl (2 extra large)', minWidth: 1536, maxWidth: null }
  ]);

  const [sqlFormatter, setSqlFormatter] = useState<SqlFormatter>({
    input: 'SELECT id, name, email \nFROM users \nWHERE status = "active" \nORDER BY created_at DESC \nLIMIT 10;',
    output: '',
    dialect: 'mysql',
    error: null
  });

  const [lengthConverter, setLengthConverter] = useState<LengthUnit>({
    value: 0,
    unit: 'm'
  });

  const [convertedLengths, setConvertedLengths] = useState<{
    mm: number;
    cm: number;
    in: number;
    m: number;
    ft: number;
    yd: number;
    km: number;
    mi: number;
  }>({
    mm: 0,
    cm: 0,
    in: 0,
    m: 0,
    ft: 0,
    yd: 0,
    km: 0,
    mi: 0
  });

  const [temperatureConverter, setTemperatureConverter] = useState<TemperatureUnit>({
    value: 0,
    unit: 'celsius'
  });

  const [convertedTemperatures, setConvertedTemperatures] = useState<{
    celsius: number;
    fahrenheit: number;
    kelvin: number;
  }>({
    celsius: 0,
    fahrenheit: 32,
    kelvin: 273.15
  });

  const [advancedColorConverter, setAdvancedColorConverter] = useState<ColorConverterAdvanced>({
    hex: '#3b82f6',
    rgb: 'rgb(59, 130, 246)',
    rgba: 'rgba(59, 130, 246, 1)',
    hsl: 'hsl(217, 91%, 60%)',
    hsla: 'hsla(217, 91%, 60%, 1)',
    cmyk: 'cmyk(76%, 47%, 0%, 4%)',
    name: 'Blue'
  });

  const [activeConverter, setActiveConverter] = useState<string>('format');

  const [base64Converter, setBase64Converter] = useState<Base64Converter>({
    input: '',
    output: '',
    mode: 'encode',
    error: null
  });

  const [urlConverter, setUrlConverter] = useState<UrlConverter>({
    input: '',
    output: '',
    mode: 'encode',
    error: null
  });

  const [jwtDecoder, setJwtDecoder] = useState<JwtDecoder>({
    token: '',
    header: '',
    payload: '',
    error: null
  });

  const [numberBaseConverter, setNumberBaseConverter] = useState<NumberBaseConverter>({
    input: '',
    fromBase: 10,
    toBase: 16,
    output: '',
    error: null
  });

  const [hashGenerator, setHashGenerator] = useState<HashGenerator>({
    input: '',
    algorithm: 'md5',
    output: ''
  });

  const [activeCard, setActiveCard] = useState<string>('jsonxml');

  const tools = [
    { id: 'convert', name: 'Format Converter', icon: FileJson },
    { id: 'units', name: 'Unit Converter', icon: Calculator },
    { id: 'colors', name: 'Color Tools', icon: Calendar },
    { id: 'sysInfo', name: 'System Info', icon: Monitor },
    { id: 'webLoad', name: 'Web Load Time', icon: Globe },
  ];

  useEffect(() => {
    if (['sysInfo', 'webLoad'].includes(activeTab)) {
      setSystemInfo(getSystemInfo());
    }
  }, [activeTab]);

  const detectFormat = (input: string): 'json' | 'yaml' | 'xml' | 'unknown' => {
    // Remove whitespace for detection
    const trimmed = input.trim();
    
    // Check if it's JSON
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || 
        (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        JSON.parse(trimmed);
        return 'json';
      } catch {
        // Not valid JSON
      }
    }
    
    // Check if it's XML
    if ((trimmed.startsWith('<?xml') || trimmed.startsWith('<')) && 
        (trimmed.includes('</') || trimmed.includes('/>'))) {
      return 'xml';
    }
    
    // Assume YAML if it has YAML-like structure and isn't JSON or XML
    if (trimmed.includes(':') && !trimmed.includes('{') && !trimmed.includes('<')) {
      return 'yaml';
    }
    
    return 'unknown';
  };

  const convertFormat = () => {
    try {
      let result = '';
      let detectedFormat = converter.sourceFormat === 'auto' 
        ? detectFormat(converter.input) 
        : converter.sourceFormat;
      
      if (detectedFormat === 'unknown') {
        throw new Error('Could not detect input format. Please specify manually.');
      }
      
      // If source and target formats are the same, just format/prettify
      if (detectedFormat === converter.format) {
        if (detectedFormat === 'json') {
          const parsed = JSON.parse(converter.input);
          result = JSON.stringify(parsed, null, converter.indentation);
        } else if (detectedFormat === 'yaml') {
          // Just re-parse and format YAML
          const parsed = jsYaml.load(converter.input);
          result = jsYaml.dump(parsed, { indent: converter.indentation });
        } else { // XML
          // For XML we'd need a proper XML formatter
          result = converter.input; // Placeholder
        }
      } 
      // Converting between formats
      else {
        let parsed: any;
        
        // First parse from source format
        if (detectedFormat === 'json') {
          parsed = JSON.parse(converter.input);
        } else if (detectedFormat === 'yaml') {
          parsed = jsYaml.load(converter.input);
        } else if (detectedFormat === 'xml') {
          // XML to JS object conversion would go here
          // This is a placeholder as we'd need a proper XML parser
          parsed = { placeholder: "XML parsing requires additional library" };
        }
        
        // Then convert to target format
      if (converter.format === 'json') {
          result = JSON.stringify(parsed, null, converter.indentation);
      } else if (converter.format === 'yaml') {
          result = jsYaml.dump(parsed, { indent: converter.indentation });
        } else { // XML
          // JS object to XML conversion would go here
          // This is a placeholder as we'd need a proper XML serializer
          result = `<?xml version="1.0" encoding="UTF-8"?>\n<root>\n  <message>XML conversion requires additional library</message>\n</root>`;
        }
      }
      
      setConverter({ ...converter, output: result, error: null });
    } catch (error) {
      setConverter({ 
        ...converter, 
        output: '', 
        error: error instanceof Error ? error.message : 'Error: Invalid format'
      });
    }
  };

  const convertTime = (value: number, fromUnit: TimeUnit['unit']) => {
    let ms = 0;
    
    // Convert to milliseconds first
    switch (fromUnit) {
      case 'ms':
        ms = value;
        break;
      case 's':
        ms = value * 1000;
        break;
      case 'min':
        ms = value * 60 * 1000;
        break;
      case 'h':
        ms = value * 60 * 60 * 1000;
        break;
    }

    // Convert to all units
    setConvertedTimes({
      ms: ms,
      s: ms / 1000,
      min: ms / (60 * 1000),
      h: ms / (60 * 60 * 1000)
    });
  };

  const convertFileSize = (value: number, fromUnit: FileSize['unit']) => {
    let bytes = 0;
    
    // Convert to bytes first
    switch (fromUnit) {
      case 'B':
        bytes = value;
        break;
      case 'KB':
        bytes = value * 1024;
        break;
      case 'MB':
        bytes = value * 1024 * 1024;
        break;
      case 'GB':
        bytes = value * 1024 * 1024 * 1024;
        break;
      case 'TB':
        bytes = value * 1024 * 1024 * 1024 * 1024;
        break;
    }

    // Convert to all units
    setConvertedSizes({
      B: bytes,
      KB: bytes / 1024,
      MB: bytes / (1024 * 1024),
      GB: bytes / (1024 * 1024 * 1024),
      TB: bytes / (1024 * 1024 * 1024 * 1024)
    });
  };

  const calculateLoadTime = (url: string) => {
    const startTime = performance.now();
    let timing: PerformanceNavigationTiming;

    fetch(url)
      .then(response => {
        timing = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        setWebLoadTime({
          dns: timing.domainLookupEnd - timing.domainLookupStart,
          tcp: timing.connectEnd - timing.connectStart,
          request: timing.responseStart - timing.requestStart,
          response: timing.responseEnd - timing.responseStart,
          processing: timing.domComplete - timing.domInteractive,
          load: timing.loadEventEnd - timing.loadEventStart,
          total: performance.now() - startTime
        });
      })
      .catch(error => {
        console.error('Error loading URL:', error);
      });
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatCode = async () => {
    try {
      let parser;
      let languageOptions: prettier.Options = {};

      // Configuración base
      const baseOptions: prettier.Options = {
        ...formatter.config,
        endOfLine: 'lf',
        trailingComma: 'es5',
      };

      // Configuración específica por lenguaje
      switch (formatter.language) {
        case 'typescript':
          parser = 'typescript';
          languageOptions = {
            parser: 'typescript',
            plugins: [parserBabel],
            semi: formatter.config.semi,
            singleQuote: formatter.config.singleQuote,
          };
          break;
        case 'javascript':
          parser = 'babel';
          languageOptions = {
            parser: 'babel',
            plugins: [parserBabel],
            semi: formatter.config.semi,
            singleQuote: formatter.config.singleQuote,
          };
          break;
        case 'html':
          parser = 'html';
          languageOptions = {
            parser: 'html',
            plugins: [parserHtml],
            htmlWhitespaceSensitivity: 'css',
            vueIndentScriptAndStyle: true,
          };
          break;
        case 'css':
          parser = 'css';
          languageOptions = {
            parser: 'css',
            plugins: [parserCss],
          };
          break;
      }

      const formattedCode = await prettier.format(formatter.input, {
        ...baseOptions,
        ...languageOptions,
      });

      setFormatter(prev => ({
        ...prev,
        output: formattedCode,
        error: null
      }));
    } catch (error) {
      setFormatter(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Error al formatear el código'
      }));
    }
  };

  const getSystemInfo = (): SystemInfo => {
    const browserInfo = detectBrowser();
    return {
      userAgent: navigator.userAgent,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      colorDepth: window.screen.colorDepth,
      devicePixelRatio: window.devicePixelRatio,
      platform: navigator.platform,
      browser: browserInfo.name,
      browserVersion: browserInfo.version,
      os: detectOS(),
      cookies: navigator.cookieEnabled,
      localStorage: !!window.localStorage,
      sessionStorage: !!window.sessionStorage,
      webWorkers: !!window.Worker,
      serviceWorkers: 'serviceWorker' in navigator,
      webGL: (() => {
        try {
          const canvas = document.createElement('canvas');
          return !!(window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
        } catch (e) {
          return false;
        }
      })(),
      webP: false,
      touchScreen: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      connection: getConnectionInfo()
    };
  };

  const hexToRgb = (hex: string): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const hexToHsl = (hex: string): string => {
    // Convert hex to rgb first
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
  };

  const detectBrowser = (): { name: string, version: string } => {
    const ua = navigator.userAgent;
    let browser = "Unknown";
    let version = "Unknown";

    // Chrome
    if (ua.includes("Chrome") && !ua.includes("Edg") && !ua.includes("OPR")) {
      browser = "Chrome";
      version = ua.match(/Chrome\/(\d+\.\d+)/)![1];
    }
    // Firefox
    else if (ua.includes("Firefox")) {
      browser = "Firefox";
      version = ua.match(/Firefox\/(\d+\.\d+)/)![1];
    }
    // Safari
    else if (ua.includes("Safari") && !ua.includes("Chrome")) {
      browser = "Safari";
      version = ua.match(/Version\/(\d+\.\d+)/)![1];
    }
    // Edge
    else if (ua.includes("Edg")) {
      browser = "Edge";
      version = ua.match(/Edg\/(\d+\.\d+)/)![1];
    }
    // Opera
    else if (ua.includes("OPR")) {
      browser = "Opera";
      version = ua.match(/OPR\/(\d+\.\d+)/)![1];
    }

    return { name: browser, version };
  };

  const detectOS = (): string => {
    const ua = navigator.userAgent;
    
    if (ua.includes("Win")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    if (ua.includes("Android")) return "Android";
    if (ua.includes("iPhone") || ua.includes("iPad")) return "iOS";
    
    return "Unknown";
  };

  const getConnectionInfo = (): SystemInfo['connection'] => {
    if ('connection' in navigator && navigator.connection) {
      const conn = navigator.connection as any;
      return {
        type: conn.type || 'unknown',
        downlink: conn.downlink || null,
        rtt: conn.rtt || null,
        effectiveType: conn.effectiveType || null,
        saveData: conn.saveData || null
      };
    }
    
    return {
      type: 'unknown',
      downlink: null,
      rtt: null,
      effectiveType: null,
      saveData: null
    };
  };

  const formatSql = () => {
    try {
      // Esto es una implementación básica, en una aplicación real
      // utilizaríamos una biblioteca de terceros o un servicio para formatear SQL
      const formattedSql = prettifySql(sqlFormatter.input, sqlFormatter.dialect);
      setSqlFormatter(prev => ({
        ...prev,
        output: formattedSql,
        error: null
      }));
    } catch (error) {
      setSqlFormatter(prev => ({
        ...prev,
        error: String(error)
      }));
    }
  };

  const prettifySql = (sql: string, dialect: SqlFormatter['dialect']): string => {
    // Lista de palabras clave que deberían estar en una nueva línea
    const keywords = ['SELECT', 'FROM', 'WHERE', 'GROUP BY', 'HAVING', 'ORDER BY', 'LIMIT', 'JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'INNER JOIN', 'UNION', 'INSERT INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE FROM'];
    
    // Palabras clave específicas del dialecto
    if (dialect === 'postgresql') {
      keywords.push('RETURNING');
    }
    
    // Normalizar el espacio en blanco
    let formatted = sql.trim().replace(/\s+/g, ' ');
    
    // Poner cada palabra clave en una nueva línea
    keywords.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
      formatted = formatted.replace(regex, `\n${keyword}`);
    });
    
    // Manejar paréntesis
    formatted = formatted.replace(/\(/g, '\n(');
    formatted = formatted.replace(/\)/g, ')\n');
    
    // Manejar comas
    formatted = formatted.replace(/,\s*/g, ',\n  ');
    
    // Agregar sangría
    const lines = formatted.split('\n');
    let indentLevel = 0;
    
    formatted = lines
      .map(line => {
        line = line.trim();
        if (!line) return '';
        
        // Reducir la sangría para líneas que cierran bloques
        if (line.includes(')') && indentLevel > 0) {
          indentLevel--;
        }
        
        // Agregar la sangría
        const indented = '  '.repeat(indentLevel) + line;
        
        // Aumentar la sangría para líneas que abren bloques
        if (line.includes('(')) {
          indentLevel++;
        }
        
        return indented;
      })
      .filter(line => line.trim())
      .join('\n');
    
    return formatted;
  };

  const convertLength = (value: number, fromUnit: LengthUnit['unit']) => {
    // Convert to meters first (common base unit)
    let meters = 0;
    
    switch (fromUnit) {
      case 'mm':
        meters = value / 1000;
        break;
      case 'cm':
        meters = value / 100;
        break;
      case 'in':
        meters = value * 0.0254;
        break;
      case 'm':
        meters = value;
        break;
      case 'ft':
        meters = value * 0.3048;
        break;
      case 'yd':
        meters = value * 0.9144;
        break;
      case 'km':
        meters = value * 1000;
        break;
      case 'mi':
        meters = value * 1609.344;
        break;
    }

    // Convert from meters to all units
    setConvertedLengths({
      mm: meters * 1000,
      cm: meters * 100,
      in: meters / 0.0254,
      m: meters,
      ft: meters / 0.3048,
      yd: meters / 0.9144,
      km: meters / 1000,
      mi: meters / 1609.344
    });
  };

  const convertTemperature = (value: number, fromUnit: TemperatureUnit['unit']) => {
    let celsius = 0;
    
    // Convert to Celsius first
    switch (fromUnit) {
      case 'celsius':
        celsius = value;
        break;
      case 'fahrenheit':
        celsius = (value - 32) * 5/9;
        break;
      case 'kelvin':
        celsius = value - 273.15;
        break;
    }

    // Convert to all units
    setConvertedTemperatures({
      celsius: celsius,
      fahrenheit: (celsius * 9/5) + 32,
      kelvin: celsius + 273.15
    });
  };

  const hexToRgba = (hex: string, alpha: number = 1): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const hexToCmyk = (hex: string): string => {
    // Remove the # if present
    hex = hex.replace('#', '');
    
    // Convert hex to RGB
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    
    // Calculate CMY
    let c = 1 - r;
    let m = 1 - g;
    let y = 1 - b;
    
    // Calculate K (black)
    const k = Math.min(c, m, y);
    
    // If k is 1, then c = m = y = 0
    if (k === 1) {
      return 'cmyk(0%, 0%, 0%, 100%)';
    }
    
    // Calculate final CMYK values
    c = ((c - k) / (1 - k)) * 100;
    m = ((m - k) / (1 - k)) * 100;
    y = ((y - k) / (1 - k)) * 100;
    const kPercent = k * 100;
    
    return `cmyk(${Math.round(c)}%, ${Math.round(m)}%, ${Math.round(y)}%, ${Math.round(kPercent)}%)`;
  };

  const convertBase64 = () => {
    try {
      let result = '';
      if (base64Converter.mode === 'encode') {
        // For browser environments
        result = btoa(base64Converter.input);
      } else {
        // For browser environments
        result = atob(base64Converter.input);
      }
      setBase64Converter({
        ...base64Converter,
        output: result,
        error: null
      });
    } catch (error) {
      setBase64Converter({
        ...base64Converter,
        output: '',
        error: error instanceof Error ? error.message : 'Invalid input for Base64 operation'
      });
    }
  };

  const convertUrl = () => {
    try {
      let result = '';
      if (urlConverter.mode === 'encode') {
        result = encodeURIComponent(urlConverter.input);
      } else {
        result = decodeURIComponent(urlConverter.input);
      }
      setUrlConverter({
        ...urlConverter,
        output: result,
        error: null
      });
    } catch (error) {
      setUrlConverter({
        ...urlConverter,
        output: '',
        error: error instanceof Error ? error.message : 'Invalid input for URL operation'
      });
    }
  };

  const decodeJwt = () => {
    try {
      const parts = jwtDecoder.token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format. Expected 3 parts separated by dots.');
      }
      
      // Decode the header (first part)
      const header = JSON.parse(atob(parts[0]));
      
      // Decode the payload (second part)
      const payload = JSON.parse(atob(parts[1]));
      
      setJwtDecoder({
        ...jwtDecoder,
        header: JSON.stringify(header, null, 2),
        payload: JSON.stringify(payload, null, 2),
        error: null
      });
    } catch (error) {
      setJwtDecoder({
        ...jwtDecoder,
        header: '',
        payload: '',
        error: error instanceof Error ? error.message : 'Invalid JWT token'
      });
    }
  };

  const convertNumberBase = () => {
    try {
      if (!numberBaseConverter.input.trim()) {
        setNumberBaseConverter({
          ...numberBaseConverter,
          output: '',
          error: null
        });
        return;
      }
      
      // Parse the input as a number in the source base
      const num = parseInt(numberBaseConverter.input, numberBaseConverter.fromBase);
      
      if (isNaN(num)) {
        throw new Error(`Invalid number for base ${numberBaseConverter.fromBase}`);
      }
      
      // Convert to the target base
      const result = num.toString(numberBaseConverter.toBase);
      
      setNumberBaseConverter({
        ...numberBaseConverter,
        output: result,
        error: null
      });
    } catch (error) {
      setNumberBaseConverter({
        ...numberBaseConverter,
        output: '',
        error: error instanceof Error ? error.message : 'Invalid number conversion'
      });
    }
  };

  const generateHash = () => {
    // This is a simplified version, you should use a proper hashing library in production
    // For the sake of this demo, we're just doing a simple hash representation
    const text = hashGenerator.input;
    
    // This is just a placeholder hash calculation for demonstration
    // Replace with actual crypto functions in a real implementation
    let hash = '';
    
    // In a real app, you would use crypto-js or a similar library
    // Here we'll just simulate hashes based on string length and content
    const simulateHash = (str: string, length: number): string => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash |= 0; // Convert to 32bit integer
      }
      
      // Convert to hex string of specified length
      return Math.abs(hash).toString(16).padStart(length, '0');
    };
    
    switch (hashGenerator.algorithm) {
      case 'md5':
        hash = simulateHash(text, 32);
        break;
      case 'sha1':
        hash = simulateHash(text, 40);
        break;
      case 'sha256':
        hash = simulateHash(text, 64);
        break;
      case 'sha512':
        hash = simulateHash(text, 128);
        break;
    }
    
    setHashGenerator({
      ...hashGenerator,
      output: hash
    });
  };

  // Mover las tarjetas de entrada y salida del Format Converter a un nivel superior
  const formatConverterPanel = (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Input
        </label>
        <textarea
          value={converter.input}
          onChange={(e) => setConverter({ ...converter, input: e.target.value })}
          placeholder="Paste your JSON, YAML, or XML here"
          className="w-full h-64 p-4 rounded-lg border dark:bg-gray-800 dark:border-gray-600 font-mono dark:text-white"
        />
      </div>
      
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Output
        </label>
        <textarea
          value={converter.output}
          readOnly
          placeholder="Converted output will appear here"
          className="w-full h-64 p-4 rounded-lg border dark:bg-gray-800 dark:border-gray-600 font-mono bg-gray-50 dark:bg-gray-900 dark:text-white"
        />
        {converter.output && (
          <div className="flex justify-end">
            <button
              onClick={() => {
                navigator.clipboard.writeText(converter.output);
              }}
              className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-white px-3 py-1 rounded-lg text-sm flex items-center gap-1"
            >
              <Clipboard size={16} />
              <span>Copy to Clipboard</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // Actualizar automáticamente las conversiones de base numérica
  useEffect(() => {
    convertNumberBase();
  }, [numberBaseConverter.input, numberBaseConverter.fromBase]);

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-stone-800 dark:text-white">
            Developer Tools
          </h2>
        </div>

        {/* Tool Selector */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 dark:border-gray-700 pb-3">
          <button
            onClick={() => setActiveCard('jsonxml')}
            className={`px-3 py-1.5 rounded-md text-sm ${
              activeCard === 'jsonxml' 
                ? 'bg-blue-500 dark:bg-blue-900 text-white dark:text-blue-200' 
                : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            🔄 JSON/YAML/XML
          </button>
          <button
            onClick={() => setActiveCard('base64')}
            className={`px-3 py-1.5 rounded-md text-sm ${
              activeCard === 'base64' 
                ? 'bg-blue-500 dark:bg-blue-900 text-white dark:text-blue-200' 
                : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            🔐 Base64
          </button>
          <button
            onClick={() => setActiveCard('url')}
            className={`px-3 py-1.5 rounded-md text-sm ${
              activeCard === 'url' 
                ? 'bg-blue-500 dark:bg-blue-900 text-white dark:text-blue-200' 
                : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            🔗 URL Encoder/Decoder
          </button>
          <button
            onClick={() => setActiveCard('jwt')}
            className={`px-3 py-1.5 rounded-md text-sm ${
              activeCard === 'jwt' 
                ? 'bg-blue-500 dark:bg-blue-900 text-white dark:text-blue-200' 
                : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            🔑 JWT Decoder
          </button>
          <button
            onClick={() => setActiveCard('numbase')}
            className={`px-3 py-1.5 rounded-md text-sm ${
              activeCard === 'numbase' 
                ? 'bg-blue-500 dark:bg-blue-900 text-white dark:text-blue-200' 
                : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            🔢 Number Base
          </button>
          <button
            onClick={() => setActiveCard('hash')}
            className={`px-3 py-1.5 rounded-md text-sm ${
              activeCard === 'hash' 
                ? 'bg-blue-500 dark:bg-blue-900 text-white dark:text-blue-200' 
                : 'hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            🔒 Hash Generator
          </button>
        </div>

        <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
          {/* JSON/YAML/XML Converter */}
          {activeCard === 'jsonxml' && (
            <>
              <h3 className="text-xl font-bold text-stone-800 dark:text-white mb-4">
                🔄 JSON/YAML/XML Converter
              </h3>

              <div className="flex flex-wrap gap-3 mb-4">
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Input Format
                  </label>
                  <select
                    value={converter.sourceFormat}
                    onChange={(e) => setConverter({ ...converter, sourceFormat: e.target.value as 'json' | 'yaml' | 'xml' | 'auto' })}
                    className="px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                  >
                    <option value="auto">Auto Detect</option>
                    <option value="json">JSON</option>
                    <option value="yaml">YAML</option>
                    <option value="xml">XML</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Output Format
                  </label>
                  <select
                    value={converter.format}
                    onChange={(e) => setConverter({ ...converter, format: e.target.value as 'json' | 'yaml' | 'xml' })}
                    className="px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                  >
                    <option value="json">JSON</option>
                    <option value="yaml">YAML</option>
                    <option value="xml">XML</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Indentation
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="8"
                    value={converter.indentation}
                    onChange={(e) => setConverter({ ...converter, indentation: parseInt(e.target.value) })}
                    className="w-16 px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                  />
                </div>
                
                <div className="self-end">
                  <button
                    onClick={convertFormat}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  >
                    <FileJson size={20} />
                    <span>Convert</span>
                  </button>
                </div>
                
                <div className="self-end">
                  <button
                    onClick={() => setConverter({...converter, input: '', output: '', error: null})}
                    className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  >
                    <RefreshCw size={20} />
                    <span>Clear</span>
                  </button>
                </div>
              </div>

              {converter.error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  <p>{converter.error}</p>
                </div>
              )}

              {formatConverterPanel}

              {/* Formatting Options */}
              <div className="mt-6 bg-stone-100 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-stone-800 dark:text-white mb-3">
                  Formatting Options
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="useTabs"
                      checked={formatter.config.useTabs}
                      onChange={(e) => setFormatter(prev => ({
                        ...prev,
                        config: { ...prev.config, useTabs: e.target.checked }
                      }))}
                      className="rounded border-stone-300 dark:border-gray-600"
                    />
                    <label htmlFor="useTabs" className="text-sm text-stone-700 dark:text-gray-300">
                      Use Tabs
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                      Tab Width
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="8"
                      value={formatter.config.tabWidth}
                      onChange={(e) => setFormatter(prev => ({
                        ...prev,
                        config: { ...prev.config, tabWidth: parseInt(e.target.value) }
                      }))}
                      className="w-20 px-2 py-1 rounded border dark:bg-gray-800 dark:border-gray-600"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                      Print Width
                    </label>
                    <input
                      type="number"
                      min="40"
                      max="120"
                      value={formatter.config.printWidth}
                      onChange={(e) => setFormatter(prev => ({
                        ...prev,
                        config: { ...prev.config, printWidth: parseInt(e.target.value) }
                      }))}
                      className="w-20 px-2 py-1 rounded border dark:bg-gray-800 dark:border-gray-600"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="semi"
                      checked={formatter.config.semi}
                      onChange={(e) => setFormatter(prev => ({
                        ...prev,
                        config: { ...prev.config, semi: e.target.checked }
                      }))}
                      className="rounded border-stone-300 dark:border-gray-600"
                    />
                    <label htmlFor="semi" className="text-sm text-stone-700 dark:text-gray-300">
                      Semicolons
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="singleQuote"
                      checked={formatter.config.singleQuote}
                      onChange={(e) => setFormatter(prev => ({
                        ...prev,
                        config: { ...prev.config, singleQuote: e.target.checked }
                      }))}
                      className="rounded border-stone-300 dark:border-gray-600"
                    />
                    <label htmlFor="singleQuote" className="text-sm text-stone-700 dark:text-gray-300">
                      Single Quotes
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="bracketSpacing"
                      checked={formatter.config.bracketSpacing}
                      onChange={(e) => setFormatter(prev => ({
                        ...prev,
                        config: { ...prev.config, bracketSpacing: e.target.checked }
                      }))}
                      className="rounded border-stone-300 dark:border-gray-600"
                    />
                    <label htmlFor="bracketSpacing" className="text-sm text-stone-700 dark:text-gray-300">
                      Bracket Spacing
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="bracketSameLine"
                      checked={formatter.config.bracketSameLine}
                      onChange={(e) => setFormatter(prev => ({
                        ...prev,
                        config: { ...prev.config, bracketSameLine: e.target.checked }
                      }))}
                      className="rounded border-stone-300 dark:border-gray-600"
                    />
                    <label htmlFor="bracketSameLine" className="text-sm text-stone-700 dark:text-gray-300">
                      Bracket Same Line
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                      Arrow Function Parentheses
                    </label>
                    <select
                      value={formatter.config.arrowParens}
                      onChange={(e) => setFormatter(prev => ({
                        ...prev,
                        config: { ...prev.config, arrowParens: e.target.value as 'avoid' | 'always' }
                      }))}
                      className="w-full px-2 py-1 rounded border dark:bg-gray-800 dark:border-gray-600"
                    >
                      <option value="always">Always</option>
                      <option value="avoid">Avoid</option>
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Base64 Converter */}
          {activeCard === 'base64' && (
            <>
              <h3 className="text-xl font-bold text-stone-800 dark:text-white mb-4">
                🔐 Base64 Converter
              </h3>
              <div className="flex flex-wrap gap-3 mb-4">
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Mode
                  </label>
                  <select
                    value={base64Converter.mode}
                    onChange={(e) => setBase64Converter({ 
                      ...base64Converter, 
                      mode: e.target.value as 'encode' | 'decode' 
                    })}
                    className="px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                  >
                    <option value="encode">Encode</option>
                    <option value="decode">Decode</option>
                  </select>
                </div>
                
                <div className="self-end">
                  <button
                    onClick={convertBase64}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  >
                    <FileJson size={20} />
                    <span>{base64Converter.mode === 'encode' ? 'Encode' : 'Decode'}</span>
                  </button>
                </div>
                
                <div className="self-end">
                  <button
                    onClick={() => setBase64Converter({...base64Converter, input: '', output: '', error: null})}
                    className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  >
                    <RefreshCw size={20} />
                    <span>Clear</span>
                  </button>
                </div>
              </div>
              
              {base64Converter.error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  <p>{base64Converter.error}</p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Input
                  </label>
                  <textarea
                    value={base64Converter.input}
                    onChange={(e) => setBase64Converter({ ...base64Converter, input: e.target.value })}
                    placeholder={base64Converter.mode === 'encode' ? 'Enter text to encode' : 'Enter Base64 to decode'}
                    className="w-full h-64 p-4 rounded-lg border dark:bg-gray-800 dark:border-gray-600 font-mono dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Output
                  </label>
                  <textarea
                    value={base64Converter.output}
                    readOnly
                    placeholder="Result will appear here"
                    className="w-full h-64 p-4 rounded-lg border dark:bg-gray-800 dark:border-gray-600 font-mono bg-gray-50 dark:bg-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </>
          )}

          {/* URL Encoder/Decoder */}
          {activeCard === 'url' && (
            <>
              <h3 className="text-xl font-bold text-stone-800 dark:text-white mb-4">
                🔗 URL Encoder/Decoder
              </h3>
              <div className="flex flex-wrap gap-3 mb-4">
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Mode
                  </label>
                  <select
                    value={urlConverter.mode}
                    onChange={(e) => setUrlConverter({ 
                      ...urlConverter, 
                      mode: e.target.value as 'encode' | 'decode' 
                    })}
                    className="px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                  >
                    <option value="encode">Encode</option>
                    <option value="decode">Decode</option>
                  </select>
                </div>
                
                <div className="self-end">
                  <button
                    onClick={convertUrl}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  >
                    <FileJson size={20} />
                    <span>{urlConverter.mode === 'encode' ? 'Encode' : 'Decode'}</span>
                  </button>
                </div>
                
                <div className="self-end">
                  <button
                    onClick={() => setUrlConverter({...urlConverter, input: '', output: '', error: null})}
                    className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  >
                    <RefreshCw size={20} />
                    <span>Clear</span>
                  </button>
                </div>
              </div>
              
              {urlConverter.error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  <p>{urlConverter.error}</p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Input
                  </label>
                  <textarea
                    value={urlConverter.input}
                    onChange={(e) => setUrlConverter({ ...urlConverter, input: e.target.value })}
                    placeholder={urlConverter.mode === 'encode' ? 'Enter text to URL encode' : 'Enter URL encoded text to decode'}
                    className="w-full h-64 p-4 rounded-lg border dark:bg-gray-800 dark:border-gray-600 font-mono dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Output
                  </label>
                  <textarea
                    value={urlConverter.output}
                    readOnly
                    placeholder="Result will appear here"
                    className="w-full h-64 p-4 rounded-lg border dark:bg-gray-800 dark:border-gray-600 font-mono bg-gray-50 dark:bg-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </>
          )}

          {/* JWT Decoder */}
          {activeCard === 'jwt' && (
            <>
              <h3 className="text-xl font-bold text-stone-800 dark:text-white mb-4">
                🔑 JWT Decoder
              </h3>
              <div className="flex flex-wrap gap-3 mb-4">
                <div className="w-full md:w-2/3">
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    JWT Token
                  </label>
                  <input
                    type="text"
                    value={jwtDecoder.token}
                    onChange={(e) => setJwtDecoder({ ...jwtDecoder, token: e.target.value })}
                    placeholder="Paste your JWT token here"
                    className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 font-mono dark:text-white"
                  />
                </div>
                
                <div className="self-end">
                  <button
                    onClick={decodeJwt}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  >
                    <FileJson size={20} />
                    <span>Decode</span>
                  </button>
                </div>
                
                <div className="self-end">
                  <button
                    onClick={() => setJwtDecoder({...jwtDecoder, token: '', header: '', payload: '', error: null})}
                    className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  >
                    <RefreshCw size={20} />
                    <span>Clear</span>
                  </button>
                </div>
              </div>
              
              {jwtDecoder.error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  <p>{jwtDecoder.error}</p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Header
                  </label>
                  <textarea
                    value={jwtDecoder.header}
                    readOnly
                    placeholder="JWT header will appear here"
                    className="w-full h-64 p-4 rounded-lg border dark:bg-gray-800 dark:border-gray-600 font-mono bg-gray-50 dark:bg-gray-900 dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Payload
                  </label>
                  <textarea
                    value={jwtDecoder.payload}
                    readOnly
                    placeholder="JWT payload will appear here"
                    className="w-full h-64 p-4 rounded-lg border dark:bg-gray-800 dark:border-gray-600 font-mono bg-gray-50 dark:bg-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </>
          )}

          {/* Number Base Converter */}
          {activeCard === 'numbase' && (
            <>
              <h3 className="text-xl font-bold text-stone-800 dark:text-white mb-4">
                🔢 Number Base Converter
              </h3>
              <div className="flex flex-wrap gap-3 mb-4">
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    From Base
                  </label>
                  <select
                    value={numberBaseConverter.fromBase}
                    onChange={(e) => setNumberBaseConverter({ 
                      ...numberBaseConverter, 
                      fromBase: parseInt(e.target.value) as 2 | 8 | 10 | 16 
                    })}
                    className="px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                  >
                    <option value="2">Binary (2)</option>
                    <option value="8">Octal (8)</option>
                    <option value="10">Decimal (10)</option>
                    <option value="16">Hexadecimal (16)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Input ({numberBaseConverter.fromBase === 2 ? 'Binary' : 
                           numberBaseConverter.fromBase === 8 ? 'Octal' : 
                           numberBaseConverter.fromBase === 10 ? 'Decimal' : 'Hexadecimal'})
                  </label>
                  <input
                    type="text"
                    value={numberBaseConverter.input}
                    onChange={(e) => setNumberBaseConverter({ ...numberBaseConverter, input: e.target.value })}
                    placeholder={`Enter ${
                      numberBaseConverter.fromBase === 2 ? 'binary' : 
                      numberBaseConverter.fromBase === 8 ? 'octal' : 
                      numberBaseConverter.fromBase === 10 ? 'decimal' : 'hexadecimal'
                    } number`}
                    className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 font-mono dark:text-white"
                  />
                </div>
              </div>
              
              {numberBaseConverter.error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  <p>{numberBaseConverter.error}</p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Output (Binary)
                  </label>
                  <input
                    type="text"
                    value={parseInt(numberBaseConverter.input, numberBaseConverter.fromBase).toString(2)}
                    readOnly
                    placeholder="Binary result"
                    className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 font-mono bg-gray-50 dark:bg-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Output (Octal)
                  </label>
                  <input
                    type="text"
                    value={parseInt(numberBaseConverter.input, numberBaseConverter.fromBase).toString(8)}
                    readOnly
                    placeholder="Octal result"
                    className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 font-mono bg-gray-50 dark:bg-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Output (Decimal)
                  </label>
                  <input
                    type="text"
                    value={parseInt(numberBaseConverter.input, numberBaseConverter.fromBase).toString(10)}
                    readOnly
                    placeholder="Decimal result"
                    className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 font-mono bg-gray-50 dark:bg-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Output (Hexadecimal)
                  </label>
                  <input
                    type="text"
                    value={parseInt(numberBaseConverter.input, numberBaseConverter.fromBase).toString(16)}
                    readOnly
                    placeholder="Hexadecimal result"
                    className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 font-mono bg-gray-50 dark:bg-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </>
          )}

          {/* Hash Generator */}
          {activeCard === 'hash' && (
            <>
              <h3 className="text-xl font-bold text-stone-800 dark:text-white mb-4">
                🔒 Hash Generator
              </h3>
              <div className="flex flex-wrap gap-3 mb-4">
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Hash Algorithm
                  </label>
                  <select
                    value={hashGenerator.algorithm}
                    onChange={(e) => setHashGenerator({ 
                      ...hashGenerator, 
                      algorithm: e.target.value as 'md5' | 'sha1' | 'sha256' | 'sha512' 
                    })}
                    className="px-3 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                  >
                    <option value="md5">MD5</option>
                    <option value="sha1">SHA-1</option>
                    <option value="sha256">SHA-256</option>
                    <option value="sha512">SHA-512</option>
                  </select>
                </div>
                
                <div className="self-end">
                  <button
                    onClick={generateHash}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  >
                    <FileJson size={20} />
                    <span>Generate Hash</span>
                  </button>
                </div>
                
                <div className="self-end">
                  <button
                    onClick={() => setHashGenerator({...hashGenerator, input: '', output: ''})}
                    className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  >
                    <RefreshCw size={20} />
                    <span>Clear</span>
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Input Text
                  </label>
                  <textarea
                    value={hashGenerator.input}
                    onChange={(e) => setHashGenerator({ ...hashGenerator, input: e.target.value })}
                    placeholder="Enter text to hash"
                    className="w-full h-64 p-4 rounded-lg border dark:bg-gray-800 dark:border-gray-600 font-mono dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    Hash Output ({hashGenerator.algorithm.toUpperCase()})
                  </label>
                  <textarea
                    value={hashGenerator.output}
                    readOnly
                    placeholder="Hash will appear here"
                    className="w-full h-64 p-4 rounded-lg border dark:bg-gray-800 dark:border-gray-600 font-mono bg-gray-50 dark:bg-gray-900 dark:text-white"
                  />
                  <div className="flex mt-2">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(hashGenerator.output);
                      }}
                      className="ml-auto text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 text-sm flex items-center gap-1"
                    >
                      <Copy size={14} />
                      <span>Copy to clipboard</span>
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Note:</strong> This is a simplified hash generator for demonstration purposes. 
                  In production environments, use appropriate cryptographic libraries for secure hashing.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DevTools;